<script setup>
// SiteUp — CT-C: "Lead" no painel da conversa.
//
// Junta o que estava espalhado: a campanha DESTA conversa (gravada pelo
// CtwaReferralProcessor em conversation.custom_attributes) e a PRIMEIRA vez que
// a pessoa apareceu (gravada no contato, first-write-wins, pela CT-D).
//
// Por que seção e não aba: o painel já organiza tudo em acordeões arrastáveis e
// ocultáveis por usuário; uma barra de abas por cima substituiria esse sistema.
// Como seção, "Lead" entra na personalização como qualquer outra.
//
// O bloco WhatsApp continua sendo a seção própria dele (waha_info) — repetir
// aqui seria eco.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  // conversation.custom_attributes
  conversationAttributes: { type: Object, default: () => ({}) },
  // contact.additional_attributes
  contactAttributes: { type: Object, default: () => ({}) },
});

const { t } = useI18n();

// Campanha desta conversa: no máximo 3, só o que existir.
const campaignChips = computed(() =>
  [
    props.conversationAttributes?.utm_campaign,
    props.conversationAttributes?.utm_content,
    props.conversationAttributes?.utm_source,
  ].filter(Boolean)
);

const headline = computed(
  () => props.conversationAttributes?.ctwa_headline || ''
);

const ORIGIN_LABELS = {
  meta_ad: 'LEAD_ORIGIN.CHANNEL.META_AD',
  meta_post: 'LEAD_ORIGIN.CHANNEL.META_POST',
};

const originChannel = computed(() => {
  const channel = props.contactAttributes?.origin_channel;
  if (!channel) return '';
  return ORIGIN_LABELS[channel] ? t(ORIGIN_LABELS[channel]) : channel;
});

const originCampaign = computed(
  () => props.contactAttributes?.origin_campaign || ''
);

const originCapturedAt = computed(() => {
  const raw = props.contactAttributes?.origin_captured_at;
  if (!raw) return '';
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toLocaleDateString();
});

const hasFirstTouch = computed(() =>
  Boolean(originChannel.value || originCampaign.value || originCapturedAt.value)
);
const hasThisConversation = computed(() =>
  Boolean(campaignChips.value.length || headline.value)
);
const isEmpty = computed(
  () => !hasFirstTouch.value && !hasThisConversation.value
);
</script>

<template>
  <div class="flex flex-col gap-3 px-1 pb-1">
    <p v-if="isEmpty" class="m-0 text-xs leading-snug text-n-slate-11">
      {{ $t('LEAD_ORIGIN.EMPTY') }}
    </p>

    <section v-if="hasThisConversation" class="flex flex-col gap-1.5">
      <p
        class="m-0 text-[11px] font-bold uppercase tracking-wide text-n-slate-11"
      >
        {{ $t('LEAD_ORIGIN.THIS_CONVERSATION') }}
      </p>
      <div v-if="campaignChips.length" class="flex flex-wrap gap-1.5">
        <span
          v-for="chip in campaignChips"
          :key="chip"
          :title="chip"
          class="max-w-full truncate rounded-full bg-n-blue-3 px-2.5 py-0.5 text-[11px] font-bold text-n-blue-11"
        >
          {{ chip }}
        </span>
      </div>
      <div v-if="headline" class="flex min-w-0 items-baseline gap-2 text-xs">
        <span class="shrink-0 text-n-slate-11">
          {{ $t('LEAD_ORIGIN.AD') }}
        </span>
        <span
          class="min-w-0 flex-1 truncate text-right text-n-slate-12"
          :title="headline"
        >
          {{ headline }}
        </span>
      </div>
    </section>

    <section v-if="hasFirstTouch" class="flex flex-col gap-1.5">
      <p
        class="m-0 text-[11px] font-bold uppercase tracking-wide text-n-slate-11"
      >
        {{ $t('LEAD_ORIGIN.FIRST_TOUCH') }}
      </p>
      <div
        v-if="originChannel"
        class="flex min-w-0 items-baseline gap-2 text-xs"
      >
        <span class="shrink-0 text-n-slate-11">
          {{ $t('LEAD_ORIGIN.CHANNEL_LABEL') }}
        </span>
        <span class="min-w-0 flex-1 truncate text-right text-n-slate-12">
          {{ originChannel }}
        </span>
      </div>
      <div
        v-if="originCampaign"
        class="flex min-w-0 items-baseline gap-2 text-xs"
      >
        <span class="shrink-0 text-n-slate-11">
          {{ $t('LEAD_ORIGIN.CAMPAIGN') }}
        </span>
        <span
          class="min-w-0 flex-1 truncate text-right text-n-slate-12"
          :title="originCampaign"
        >
          {{ originCampaign }}
        </span>
      </div>
      <div
        v-if="originCapturedAt"
        class="flex min-w-0 items-baseline gap-2 text-xs"
      >
        <span class="shrink-0 text-n-slate-11">
          {{ $t('LEAD_ORIGIN.WHEN') }}
        </span>
        <span class="min-w-0 flex-1 truncate text-right text-n-slate-12">
          {{ originCapturedAt }}
        </span>
      </div>
    </section>
  </div>
</template>
